import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles } from '@/mocks/articles';

export default function Home() {
  const { t } = useTranslation();
  const featuredArticles = getFeaturedArticles(6);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '按摩视频大全',
    url: 'https://anmo.guide',
    description: '海量按摩视频教程，涵盖全身放松、穴位按摩、肩颈腰背、足底反射区等专业手法演示',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://anmo.guide/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '按摩视频大全',
    url: 'https://anmo.guide',
    description: '专业按摩视频教程平台',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '新手如何开始学习按摩？', acceptedAnswer: { '@type': 'Answer', text: '建议从全身放松按摩教程入手，了解基础的按摩手法和力度控制。然后根据自身需求选择分类教程，比如肩颈不适可以选择肩颈按摩教程。所有教程都适合居家操作，无需专业设备。' } },
      { '@type': 'Question', name: '按摩视频教程适合所有人吗？', acceptedAnswer: { '@type': 'Answer', text: '大部分教程适合健康人群日常保健使用。但如果您有严重疾病、急性损伤或特殊体质，建议在医生指导下进行按摩。孕妇进行穴位按摩前请务必咨询医生。' } },
      { '@type': 'Question', name: '按摩多久能见效？', acceptedAnswer: { '@type': 'Answer', text: '轻度不适通常在按摩后即可感受到缓解。对于慢性问题，需要坚持每天或隔天按摩，通常2-4周后效果明显。重要的是坚持和正确的手法。' } },
      { '@type': 'Question', name: '按摩时需要注意什么？', acceptedAnswer: { '@type': 'Answer', text: '按摩前保持双手清洁，力度由轻到重循序渐进。避免在饭后一小时内按摩。按摩后多喝温水，帮助代谢。如果按摩过程中出现剧烈疼痛，应立即停止。' } },
      { '@type': 'Question', name: '精油按摩需要准备什么？', acceptedAnswer: { '@type': 'Answer', text: '需要准备基础油（如甜杏仁油、葡萄籽油）和精油。精油必须稀释后使用，身体按摩比例为2-3%（10ml基础油加4-6滴精油）。使用前建议做皮肤敏感测试。' } },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20spa%20interior%20with%20soft%20candlelight%20and%20natural%20stone%20walls%2C%20bamboo%20accents%2C%20white%20linen%20on%20massage%20table%2C%20diffused%20aromatherapy%20steam%2C%20peaceful%20zen%20atmosphere%2C%20cream%20and%20warm%20amber%20tones%2C%20minimalist%20luxury%20spa%2C%20editorial%20photography%20with%20soft%20focus&width=1800&height=900&seq=hero-massage-2026&orientation=landscape"
            alt="专业按摩视频教程"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
                {t('heroTitle')}
              </h1>
              <p className="text-base md:text-lg text-background-50/80 mb-8 leading-relaxed max-w-2xl mx-auto">
                {t('heroSubtitle')}
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/category/all"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {t('heroCta')}
                </Link>
                <a
                  href="#categories"
                  className="px-6 py-3 border border-background-50/40 text-background-50 rounded-full font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  {t('heroCtaSecondary')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-video-line', value: '200+', label: t('statsVideos') },
                { icon: 'ri-user-line', value: '50,000+', label: t('statsStudents') },
                { icon: 'ri-grid-line', value: '6', label: t('statsCategories') },
                { icon: 'ri-thumb-up-line', value: '98%', label: t('statsSatisfaction') },
              ].map((stat, i) => (
                <div key={i} className="text-center p-4">
                  <i className={`${stat.icon} text-2xl text-primary-500 mb-2`}></i>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section id="categories" className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {t('categoriesTitle')}
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                {t('categoriesSubtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {cat.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10 md:mb-14">
              <div>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                  {t('featuredArticles')}
                </h2>
                <p className="text-sm text-foreground-500">精选优质按摩教程，助您快速上手</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium"
              >
                查看全部教程 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {t('featuresTitle')}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { icon: 'ri-award-line', title: t('feature1Title'), desc: t('feature1Desc') },
                { icon: 'ri-stack-line', title: t('feature2Title'), desc: t('feature2Desc') },
                { icon: 'ri-home-line', title: t('feature3Title'), desc: t('feature3Desc') },
                { icon: 'ri-4k-line', title: t('feature4Title'), desc: t('feature4Desc') },
              ].map((feat, i) => (
                <div key={i} className="text-center p-5">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${feat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {t('testimonialsTitle')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                { text: t('testimonial1Text'), name: t('testimonial1Name'), role: t('testimonial1Role') },
                { text: t('testimonial2Text'), name: t('testimonial2Name'), role: t('testimonial2Role') },
                { text: t('testimonial3Text'), name: t('testimonial3Name'), role: t('testimonial3Role') },
              ].map((item, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-1 text-primary-400 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{item.text}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {t('faqTitle')}
              </h2>
            </div>

            <div className="space-y-3">
              {[
                { q: '新手如何开始学习按摩？', a: '建议从全身放松按摩教程入手，了解基础的按摩手法和力度控制。然后根据自身需求选择分类教程，比如肩颈不适可以选择肩颈按摩教程。所有教程都适合居家操作，无需专业设备，跟着视频一步步学习即可。' },
                { q: '按摩视频教程适合所有人吗？', a: '大部分教程适合健康人群日常保健使用。但如果您有严重疾病、急性损伤或特殊体质（如骨质疏松、血栓等），建议在医生指导下进行按摩。孕妇进行穴位按摩前请务必咨询医生。' },
                { q: '按摩多久能见效？', a: '轻度不适通常在按摩后即可感受到缓解。对于慢性问题，需要坚持每天或隔天按摩，通常2-4周后效果逐渐显现。按摩是循序渐进的调理过程，重要的是坚持正确的手法。' },
                { q: '按摩时需要注意什么？', a: '按摩前保持双手清洁，指甲修剪整齐。力度由轻到重循序渐进，避免突然发力。饭后一小时内不宜按摩。按摩后多喝温水帮助代谢。如果按摩过程中出现剧烈疼痛，应立即停止并咨询专业医生。' },
                { q: '精油按摩需要准备什么？', a: '需要准备基础油（如甜杏仁油、葡萄籽油）和精油。精油必须稀释后使用，身体按摩比例为2-3%（10ml基础油加4-6滴精油）。使用前建议在手腕内侧做皮肤敏感测试，观察24小时无异常再使用。' },
              ].map((item, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {item.q}
                    </span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-14 md:py-20 bg-primary-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
              {t('ctaTitle')}
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8">
              {t('ctaSubtitle')}
            </p>
            <Link
              to="/category/all"
              className="inline-block px-8 py-3 bg-background-50 text-primary-600 rounded-full font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t('ctaButton')}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}